import {
  AnyObject,
  Encounter,
  EncounterFieldValidator,
  EncounterSection,
  EncounterSectionTiers,
  fieldTiers,
  getEncounterValidate,
  ValidationErrors,
  WithId,
} from '@hb/shared'
import { useEffect, useRef } from 'react'
import { useForm } from 'react-final-form'

const collectFieldDependencyPaths = (tiers: EncounterSectionTiers): string[] => {
  const paths: string[] = []
  for (const value of Object.values(tiers)) {
    if (typeof value === 'number') continue
    if ('type' in value) {
      const fv = value as EncounterFieldValidator
      if (fv.type === 'field') paths.push(...fv.dependsOn)
      // 'encounter-history' validators have no form field dependencies
    } else {
      paths.push(...collectFieldDependencyPaths(value))
    }
  }
  return paths
}

const buildNestedObject = (flatValues: Record<string, unknown>): AnyObject => {
  const result: AnyObject = {}
  for (const [path, value] of Object.entries(flatValues)) {
    const parts = path.split('.')
    let current = result
    for (let i = 0; i < parts.length - 1; i++) {
      if (typeof current[parts[i]] !== 'object' || current[parts[i]] === null) {
        current[parts[i]] = {}
      }
      current = current[parts[i]] as AnyObject
    }
    current[parts[parts.length - 1]] = value
  }
  return result
}

export const useEncounterValidate = (
  onNewValidate: (validate: ((data: AnyObject) => ValidationErrors) | undefined) => void,
  sections: EncounterSection[],
  encounters: WithId<Encounter>[] | null,
) => {
  const { subscribeFieldState, getFieldState } = useForm()

  const flatValuesRef = useRef<Record<string, unknown>>({})

  useEffect(() => {
    const depPaths = Array.from(
      new Set(sections.flatMap(section => collectFieldDependencyPaths(fieldTiers[section.type]))),
    )

    flatValuesRef.current = {}
    onNewValidate(undefined)

    if (depPaths.length === 0) {
      onNewValidate(getEncounterValidate(sections, {}, encounters ?? []))
      return
    }

    const pendingInit = new Set(depPaths)

    const unsubscribes = depPaths.map(path =>
      subscribeFieldState(
        path,
        () => {
          const value = getFieldState(path)?.value
          flatValuesRef.current = { ...flatValuesRef.current, [path]: value }

          if (pendingInit.has(path)) {
            pendingInit.delete(path)
            if (pendingInit.size === 0) {
              onNewValidate(
                getEncounterValidate(
                  sections,
                  buildNestedObject(flatValuesRef.current),
                  encounters ?? [],
                ),
              )
            }
            return
          }

          onNewValidate(
            getEncounterValidate(
              sections,
              buildNestedObject(flatValuesRef.current),
              encounters ?? [],
            ),
          )
        },
        { value: true },
      ),
    )

    return () => {
      unsubscribes.forEach(unsub => unsub())
    }
  }, [sections, encounters, subscribeFieldState, getFieldState, onNewValidate])
}
